import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import {
    Megaphone,
    Clock,
    Send,
    Plus,
    Trash2,
    AlertTriangle,
    PlayCircle,
    StopCircle,
    Settings2,
    RefreshCw,
    Bell
} from 'lucide-react';
import { serverStore } from '../../stores/server.store';
import { Card } from '../design/Card';
import { Button } from '../design/Button';
import { Badge } from '../design/Badge';
import { Input } from '../design/Input';
import ToggleSwitch from '../common/ToggleSwitch';
import toast from 'react-hot-toast';
import { apiFetch } from '../../utils/api';
import { confirm } from '../../stores/confirm.store';
import type { Announcement, MaintenanceMode } from '../../types';
import { McTextComposer } from '../common/McTextComposer';
import { can } from '@/utils/permissions';

interface AnnouncementsTabProps {
    currentUser: any;
}

export const AnnouncementsTab = observer(({ currentUser }: AnnouncementsTabProps) => {
    const { activeServer } = serverStore;

    // Local state for new announcement form
    const [newMessage, setNewMessage] = useState('');
    const [newInterval, setNewInterval] = useState(20);
    const [newSender, setNewSender] = useState('');

    // Maintenance mode form
    const [maintenanceMinutes, setMaintenanceMinutes] = useState(10);
    const [maintenanceReason, setMaintenanceReason] = useState('Geplante Wartung');
    const [estimatedDowntime, setEstimatedDowntime] = useState(30);
    const [quickMessage, setQuickMessage] = useState('');

    if (!can(currentUser, 'can_manage_schedules')) return null;
    if (!activeServer) return null;

    const announcements = activeServer.announcements || [];
    const maintenanceMode = activeServer.maintenance_mode || { active: false };

    const handleAddAnnouncement = async () => {
        if (!newMessage.trim()) {
            toast.error('Bitte eine Nachricht eingeben');
            return;
        }

        try {
            const res = await apiFetch(`/api/servers/${activeServer.id}/announcements`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    message: newMessage,
                    interval_minutes: newInterval,
                    sender_name: newSender,
                    enabled: true
                })
            });

            if (res.ok) {
                toast.success('Ankündigung hinzugefügt!');
                setNewMessage('');
                setNewSender('');
                serverStore.fetchData();
            } else {
                toast.error('Fehler beim Hinzufügen');
            }
        } catch (e) {
            toast.error('Netzwerkfehler');
        }
    };

    const handleToggleAnnouncement = async (id: string, enabled: boolean) => {
        try {
            const res = await apiFetch(`/api/servers/${activeServer.id}/announcements/${id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ enabled })
            });

            if (res.ok) {
                serverStore.fetchData();
            }
        } catch (e) {
            toast.error('Fehler beim Aktualisieren');
        }
    };

    const handleDeleteAnnouncement = async (id: string) => {
        if (!await confirm({
            title: 'Ankündigung löschen?',
            message: 'Möchtest du diese Ankündigung wirklich unwiderruflich löschen?',
            confirmText: 'Löschen',
            variant: 'danger',
            icon: 'trash'
        })) return;

        try {
            const res = await apiFetch(`/api/servers/${activeServer.id}/announcements/${id}`, {
                method: 'DELETE'
            });

            if (res.ok) {
                toast.success('Ankündigung gelöscht');
                serverStore.fetchData();
            }
        } catch (e) {
            toast.error('Fehler beim Löschen');
        }
    };

    const handleStartMaintenance = async () => {
        if (!await confirm({
            title: 'Wartungsmodus starten?',
            message: `Möchtest du den Wartungsmodus für diesen Server in ${maintenanceMinutes} Minuten starten? Alle Spieler werden gewarnt und der Server wird heruntergefahren.`,
            confirmText: 'Wartung planen',
            variant: 'warning',
            icon: 'alert'
        })) return;

        try {
            const res = await apiFetch(`/api/servers/${activeServer.id}/maintenance`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    minutes_until_start: maintenanceMinutes,
                    estimated_downtime_minutes: estimatedDowntime,
                    reason: maintenanceReason
                })
            });

            if (res.ok) {
                toast.success('Wartungsmodus geplant!');
                serverStore.fetchData();
            } else {
                toast.error('Fehler beim Starten');
            }
        } catch (e) {
            toast.error('Netzwerkfehler');
        }
    };

    const handleCancelMaintenance = async () => {
        try {
            const res = await apiFetch(`/api/servers/${activeServer.id}/maintenance`, {
                method: 'DELETE'
            });

            if (res.ok) {
                toast.success('Wartungsmodus abgebrochen');
                serverStore.fetchData();
            }
        } catch (e) {
            toast.error('Fehler beim Abbrechen');
        }
    };

    const handleUpdateMaintenance = async () => {
        try {
            const res = await apiFetch(`/api/servers/${activeServer.id}/maintenance`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    estimated_downtime_minutes: estimatedDowntime,
                    reason: maintenanceReason,
                    minutes_until_shutdown: maintenanceMinutes
                })
            });

            if (res.ok) {
                toast.success('Wartung aktualisiert');
                serverStore.fetchData();
            } else {
                toast.error('Fehler beim Aktualisieren');
            }
        } catch (e) {
            toast.error('Netzwerkfehler');
        }
    };

    const handleCompleteMaintenance = async () => {
        try {
            const res = await apiFetch(`/api/servers/${activeServer.id}/maintenance/complete`, {
                method: 'POST'
            });

            if (res.ok) {
                toast.success('Wartung abgeschlossen!');
                serverStore.fetchData();
            } else {
                toast.error('Fehler beim Abschließen');
            }
        } catch (e) {
            toast.error('Netzwerkfehler');
        }
    };

    const handleSendNow = async (message: string) => {
        try {
            const res = await apiFetch(`/api/servers/${activeServer.id}/command`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ command: `say ${message}` })
            });

            if (res.ok) {
                toast.success('Nachricht gesendet!');
            }
        } catch (e) {
            toast.error('Fehler beim Senden');
        }
    };

    const handleSendActionBar = async (message: string, totalSeconds: number) => {
        try {
            const formatTime = (seconds: number): string => {
                if (seconds >= 3600) {
                    const h = Math.floor(seconds / 3600);
                    const m = Math.floor((seconds % 3600) / 60);
                    const s = seconds % 60;
                    return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
                } else if (seconds >= 60) {
                    const m = Math.floor(seconds / 60);
                    const s = seconds % 60;
                    return `${m}:${s.toString().padStart(2, '0')}`;
                } else {
                    return `${seconds}s`;
                }
            };

            const displayTime = totalSeconds >= 60 ? `${Math.floor(totalSeconds / 60)}m ${totalSeconds % 60}s` : `${totalSeconds}s`;

            const totalBars = 20;

            // Send command with countdown for each second
            for (let i = 0; i < totalSeconds; i++) {
                const remainingSeconds = totalSeconds - i;
                const progress = (totalSeconds - remainingSeconds) / totalSeconds;
                const filledCount = Math.floor(progress * totalBars);
                const emptyCount = totalBars - filledCount;
                
                const filledBars = '█'.repeat(filledCount);
                const emptyBars = '░'.repeat(emptyCount);
                
                const barColor = remainingSeconds <= 30 ? "red" : (remainingSeconds <= 120 ? "yellow" : "green");
                const timeColor = remainingSeconds <= 30 ? "dark_red" : (remainingSeconds <= 120 ? "gold" : "yellow");
                const timeStr = formatTime(remainingSeconds);
                
                setTimeout(async () => {
                    try {
                        const actionBarCmd = `title @a actionbar ["",{"text":"${filledBars}","color":"${barColor}"},{"text":"${emptyBars}","color":"dark_gray"},{"text":" ${message.replace(/"/g, '\\"')} ","color":"white"},{"text":"${timeStr}","color":"${timeColor}","bold":true}]`;
                        
                        // Only log the very first one (i=0), skip the rest
                        const shouldLog = (i === 0);
                        
                        await apiFetch(`/api/servers/${activeServer.id}/command`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ 
                                command: actionBarCmd, 
                                skipAudit: !shouldLog 
                            })
                        });
                    } catch (e) {}
                }, i * 1000);
            }
            
            toast.success(`Action Bar Countdown für ${displayTime} gestartet!`);
            
            // Wait, if I send the command once without skipAudit, it shows up in game.
            // If I then start the loop, I have duplicate.
            
            // I will assume the previous 'skipAudit' didn't work because of a build issue or backend logic error.
            // I will forcefully update frontend to ensure `skipAudit` is passed.
            
            
            // Wait, if I send the command once without skipAudit, it shows up in game.
            // If I then start the loop, I have duplicate.
            
            // I will assume the previous 'skipAudit' didn't work because of a build issue or backend logic error.
            // I will forcefully update frontend to ensure `skipAudit` is passed.
        } catch (e) {
            toast.error('Fehler beim Senden');
        }
    };

    return (
        <div className="space-y-6 animate-in fade-in duration-300">
            {/* Header */}
            <div>
                <h3 className="font-display text-xl font-semibold text-fg tracking-tight flex items-center gap-2">
                    <Megaphone className="text-yellow-400" size={22} />
                    Ankündigungen & Wartung
                </h3>
                <p className="text-xs md:text-sm text-fg-muted mt-1">
                    Automatische Ankündigungen und Wartungsplanung für <span className="text-yellow-400">{activeServer.name}</span>
                </p>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                {/* Recurring Announcements */}
                <Card>
                    <h4 className="text-sm font-semibold text-fg mb-4 flex items-center">
                        <Clock size={18} className="mr-2 text-blue-400" />
                        Wiederkehrende Ankündigungen
                    </h4>


                    <div className="space-y-4 mb-6 p-4 bg-black/20 rounded-xl border border-line">
                        <McTextComposer
                            label="Nachricht"
                            value={newMessage}
                            onChange={setNewMessage}
                            placeholder="z.B. &cStream &fläuft auf &btwitch.tv/…"
                        />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-[10px] uppercase text-fg-muted font-bold mb-1.5 ml-1">
                                    Absender Name (Optional)
                                </label>
                                <input
                                    type="text"
                                    value={newSender}
                                    onChange={(e) => setNewSender(e.target.value)}
                                    placeholder="z.B. Server, Admin, Bot..."
                                    className="w-full bg-ink/50 border border-line-strong rounded-xl p-3 text-white text-sm outline-none focus:border-blue-500/50"
                                />
                            </div>
                            
                            <div>
                                <label className="block text-[10px] uppercase text-fg-muted font-bold mb-1.5 ml-1">
                                    Intervall (Minuten)
                                </label>
                                <input
                                    type="number"
                                    min={1}
                                    max={120}
                                    value={newInterval}
                                    onChange={(e) => setNewInterval(parseInt(e.target.value) || 20)}
                                    className="w-full bg-ink/50 border border-line-strong rounded-xl p-3 text-white text-sm outline-none focus:border-blue-500/50"
                                />
                            </div>
                        </div>

                        <Button onClick={handleAddAnnouncement} variant="primary" className="w-full h-[46px]">
                            <Plus size={16} className="mr-1" /> Ankündigung hinzufügen
                        </Button>
                    </div>

                    {/* List of announcements */}
                    {announcements.length > 0 ? (
                        <div className="space-y-2">
                            {announcements.map((ann: Announcement) => (
                                <div
                                    key={ann.id}
                                    className="flex items-center justify-between p-3 bg-black/20 rounded-xl border border-line hover:border-line-strong transition-colors"
                                >
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm text-white truncate">{ann.message}</div>
                                        <div className="text-xs text-fg-muted flex items-center gap-2 mt-1">
                                            <Clock size={12} />
                                            Alle {ann.interval_minutes} Minuten
                                            {ann.sender_name && (
                                                <span className="text-fg-muted">
                                                    • Von: <span className="text-white">{ann.sender_name}</span>
                                                </span>
                                            )}
                                            {ann.last_sent && (
                                                <span className="text-fg-muted">
                                                    • Zuletzt: {new Date(ann.last_sent).toLocaleTimeString('de-DE')}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 ml-3">
                                        <Button
                                            onClick={() => handleSendNow(ann.message)}
                                            variant="ghost"
                                            size="icon"
                                            className="hover:text-blue-400 hover:bg-blue-500/10"
                                            title="Jetzt senden"
                                        >
                                            <Send size={14} />
                                        </Button>
                                        <ToggleSwitch
                                            enabled={ann.enabled}
                                            onChange={(val) => handleToggleAnnouncement(ann.id, val)}
                                        />
                                        <Button
                                            onClick={() => handleDeleteAnnouncement(ann.id)}
                                            variant="ghost"
                                            size="icon"
                                            className="hover:text-red-400 hover:bg-red-500/10"
                                        >
                                            <Trash2 size={14} />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center text-fg-muted text-sm py-6">
                            Keine Ankündigungen konfiguriert
                        </div>
                    )}
                </Card>

                {/* Maintenance Mode */}
                <Card id="ann-wartung" className="scroll-mt-6">
                    <h4 className="text-sm font-semibold text-fg mb-4 flex items-center">
                        <AlertTriangle size={18} className="mr-2 text-orange-400" />
                        Wartungsmodus – nur dieser Server
                    </h4>

                    {maintenanceMode.active ? (
                        <div className="space-y-4">
                            <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-xl">
                                <div className="flex items-center gap-2 text-orange-400 font-bold mb-2">
                                    <AlertTriangle size={18} />
                                    Wartungsmodus aktiv
                                </div>
                                <div className="text-sm text-fg space-y-1">
                                    {maintenanceMode.scheduled_at && (
                                        <p>
                                            Server wird heruntergefahren um:{' '}
                                            <span className="text-white font-mono">
                                                {new Date(maintenanceMode.scheduled_at).toLocaleTimeString('de-DE')}
                                            </span>
                                        </p>
                                    )}
                                    {maintenanceMode.estimated_downtime_minutes && (
                                        <p>
                                            Geschätzte Dauer: <span className="text-white">{maintenanceMode.estimated_downtime_minutes} Minuten</span>
                                        </p>
                                    )}
                                    {maintenanceMode.reason && (
                                        <p>
                                            Grund: <span className="text-white">{maintenanceMode.reason}</span>
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="p-4 bg-black/20 rounded-xl border border-line space-y-3">
                                <h5 className="text-xs uppercase text-fg-muted font-bold">Wartung aktualisieren</h5>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-[10px] uppercase text-fg-muted font-bold mb-1.5 ml-1">
                                            Restzeit (Min.)
                                        </label>
                                        <input
                                            type="number"
                                            min={1}
                                            max={60}
                                            value={maintenanceMinutes}
                                            onChange={(e) => setMaintenanceMinutes(parseInt(e.target.value) || 10)}
                                            className="w-full bg-ink/50 border border-line-strong rounded-xl p-3 text-white text-sm outline-none focus:border-orange-500/50"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] uppercase text-fg-muted font-bold mb-1.5 ml-1">
                                            Dauer (Min.)
                                        </label>
                                        <input
                                            type="number"
                                            min={5}
                                            max={240}
                                            value={estimatedDowntime}
                                            onChange={(e) => setEstimatedDowntime(parseInt(e.target.value) || 30)}
                                            className="w-full bg-ink/50 border border-line-strong rounded-xl p-3 text-white text-sm outline-none focus:border-orange-500/50"
                                        />
                                    </div>
                                </div>
                                <Button onClick={handleUpdateMaintenance} variant="secondary" className="w-full">
                                    <Settings2 size={16} className="mr-2" /> Aktualisieren
                                </Button>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <Button onClick={handleCompleteMaintenance} variant="success" className="w-full">
                                    <PlayCircle size={16} className="mr-2" /> Abschließen
                                </Button>
                                <Button onClick={handleCancelMaintenance} variant="outline" className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10">
                                    <StopCircle size={16} className="mr-2" /> Abbrechen
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <p className="text-sm text-fg-muted">
                                Plant einen Wartungsmodus mit automatischen Warnungen (10min, 5min, 1min vor Herunterfahren).
                            </p>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-[10px] uppercase text-fg-muted font-bold mb-1.5 ml-1">
                                        Start in (Min.)
                                    </label>
                                    <input
                                        type="number"
                                        min={1}
                                        max={60}
                                        value={maintenanceMinutes}
                                        onChange={(e) => setMaintenanceMinutes(parseInt(e.target.value) || 10)}
                                        className="w-full bg-ink/50 border border-line-strong rounded-xl p-3 text-white text-sm outline-none focus:border-orange-500/50"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] uppercase text-fg-muted font-bold mb-1.5 ml-1">
                                        Dauer (Min.)
                                    </label>
                                    <input
                                        type="number"
                                        min={5}
                                        max={240}
                                        value={estimatedDowntime}
                                        onChange={(e) => setEstimatedDowntime(parseInt(e.target.value) || 30)}
                                        className="w-full bg-ink/50 border border-line-strong rounded-xl p-3 text-white text-sm outline-none focus:border-orange-500/50"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-[10px] uppercase text-fg-muted font-bold mb-1.5 ml-1">
                                    Grund
                                </label>
                                <input
                                    type="text"
                                    value={maintenanceReason}
                                    onChange={(e) => setMaintenanceReason(e.target.value)}
                                    placeholder="z.B. Server Update"
                                    className="w-full bg-ink/50 border border-line-strong rounded-xl p-3 text-white text-sm outline-none focus:border-orange-500/50"
                                />
                            </div>

                            <Button onClick={handleStartMaintenance} variant="warning" className="w-full">
                                <PlayCircle size={16} className="mr-2" /> Wartung planen
                            </Button>
                        </div>
                    )}
                </Card>
            </div>

            {/* Quick Send with Chat/Action Bar */}
            <Card>
                <h4 className="text-sm font-semibold text-fg mb-4 flex items-center">
                    <Send size={18} className="mr-2 text-green-400" />
                    Schnellnachricht senden
                </h4>
                
                <div className="space-y-4">
                    <McTextComposer
                        value={quickMessage}
                        onChange={setQuickMessage}
                        placeholder="Nachricht an alle Spieler …"
                    />
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Chat Send */}
                        <div className="p-4 bg-blue-500/5 rounded-xl border border-blue-500/20 space-y-3">
                            <div className="flex items-center gap-2">
                                <span className="text-xl">💬</span>
                                <div>
                                    <div className="text-sm font-medium text-blue-300">Chat Nachricht</div>
                                    <div className="text-[10px] text-fg-muted">Sichtbar im Chat-Verlauf</div>
                                </div>
                            </div>
                            <Button
                                onClick={() => {
                                    if (quickMessage.trim()) {
                                        handleSendNow(quickMessage);
                                        setQuickMessage('');
                                    }
                                }}
                                variant="secondary"
                                className="w-full bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 border-blue-500/30"
                            >
                                <Send size={14} className="mr-2" /> Als Chat senden
                            </Button>
                        </div>
                        
                        {/* Action Bar Send */}
                        <div className="p-4 bg-ember/5 rounded-xl border border-ember/20 space-y-3">
                            <div className="flex items-center gap-2">
                                <span className="text-xl">📊</span>
                                <div>
                                    <div className="text-sm font-medium text-ember">Action Bar</div>
                                    <div className="text-[10px] text-fg-muted">Über der Hotbar angezeigt</div>
                                </div>
                            </div>
                            <div className="space-y-3">
                                <div className="grid grid-cols-3 gap-2">
                                    <div>
                                        <div className="text-[9px] text-fg-muted mb-1 text-center">Stunden</div>
                                        <input
                                            type="number"
                                            id="quick-actionbar-hours"
                                            min={0}
                                            max={23}
                                            defaultValue={0}
                                            className="w-full bg-black/40 border border-line-strong rounded-lg px-2 py-2 text-white text-sm text-center outline-none focus:border-ember/50"
                                        />
                                    </div>
                                    <div>
                                        <div className="text-[9px] text-fg-muted mb-1 text-center">Minuten</div>
                                        <input
                                            type="number"
                                            id="quick-actionbar-minutes"
                                            min={0}
                                            max={59}
                                            defaultValue={5}
                                            className="w-full bg-black/40 border border-line-strong rounded-lg px-2 py-2 text-white text-sm text-center outline-none focus:border-ember/50"
                                        />
                                    </div>
                                    <div>
                                        <div className="text-[9px] text-fg-muted mb-1 text-center">Sekunden</div>
                                        <input
                                            type="number"
                                            id="quick-actionbar-seconds"
                                            min={0}
                                            max={59}
                                            defaultValue={0}
                                            className="w-full bg-black/40 border border-line-strong rounded-lg px-2 py-2 text-white text-sm text-center outline-none focus:border-ember/50"
                                        />
                                    </div>
                                </div>
                                <Button
                                    onClick={() => {
                                        const hoursInput = document.getElementById('quick-actionbar-hours') as HTMLInputElement;
                                        const minutesInput = document.getElementById('quick-actionbar-minutes') as HTMLInputElement;
                                        const secondsInput = document.getElementById('quick-actionbar-seconds') as HTMLInputElement;
                                        
                                        if (quickMessage.trim()) {
                                            const hours = parseInt(hoursInput?.value) || 0;
                                            const minutes = parseInt(minutesInput?.value) || 0;
                                            const seconds = parseInt(secondsInput?.value) || 0;
                                            const totalSeconds = hours * 3600 + minutes * 60 + seconds;
                                            
                                            if (totalSeconds < 1) {
                                                toast.error('Bitte mindestens 1 Sekunde einstellen');
                                                return;
                                            }
                                            if (totalSeconds > 3600) {
                                                toast.error('Maximal 1 Stunde erlaubt');
                                                return;
                                            }
                                            
                                            handleSendActionBar(quickMessage, totalSeconds);
                                            setQuickMessage('');
                                        } else {
                                            toast.error('Bitte eine Nachricht eingeben');
                                        }
                                    }}
                                    variant="secondary"
                                    className="w-full bg-ember/15 hover:bg-ember/25 text-ember border-ember/30"
                                >
                                    <Send size={14} className="mr-2" /> Action Bar Countdown starten
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </Card>

            {/* Scheduled Restarts */}
            <Card id="ann-neustarts" className="scroll-mt-6">
                <h4 className="text-sm font-semibold text-fg mb-4 flex items-center">
                    <RefreshCw size={18} className="mr-2 text-ember" />
                    Geplante Neustarts
                </h4>
                <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-black/20 rounded-xl border border-line">
                        <div>
                            <div className="text-sm font-medium text-fg">Automatische Neustarts</div>
                            <div className="text-xs text-fg-muted mt-0.5">Startet den Server zu festgelegten Uhrzeiten neu</div>
                        </div>
                        <ToggleSwitch
                            enabled={activeServer.scheduled_restarts?.enabled || false}
                            onChange={(val) => {
                                serverStore.handleSaveServer({
                                    id: activeServer.id,
                                    scheduled_restarts: {
                                        ...(activeServer.scheduled_restarts || { schedules: [], warnings: [10, 5, 1] }),
                                        enabled: val
                                    }
                                });
                            }}
                        />
                    </div>

                    {activeServer.scheduled_restarts?.enabled && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
                            {/* Times */}
                            <div className="p-4 bg-gradient-to-br from-blue-500/5 to-ember/5 rounded-xl border border-blue-500/20">
                                <div className="flex items-center gap-2 mb-4">
                                    <Clock size={16} className="text-blue-400" />
                                    <h5 className="text-sm font-semibold text-blue-300">Restart-Uhrzeiten</h5>
                                </div>
                                <div className="space-y-2">
                                    {[...(activeServer.scheduled_restarts?.schedules || [])].length === 0 ? (
                                        <div className="text-xs text-fg-muted italic py-3 text-center">Keine Uhrzeiten konfiguriert</div>
                                    ) : (
                                        [...(activeServer.scheduled_restarts?.schedules || [])].map((time, idx) => (
                                            <div key={idx} className="flex items-center justify-between p-3 bg-black/30 rounded-lg border border-line group hover:border-blue-500/30 transition-colors">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                                                        <Clock size={14} className="text-blue-400" />
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span className="font-mono text-white font-medium">{time} Uhr</span>
                                                        {(() => {
                                                            try {
                                                                const parts = time.split(':').map(Number);
                                                                const h = parts[0] || 0;
                                                                const m = parts[1] || 0;
                                                                const now = new Date();
                                                                const scheduledDate = new Date();
                                                                scheduledDate.setHours(h, m, 0, 0);
                                                                
                                                                // Use safe buffer for day calculation
                                                                if (scheduledDate.getTime() <= now.getTime() - 5000) {
                                                                    scheduledDate.setDate(scheduledDate.getDate() + 1);
                                                                }
                                                                
                                                                const scheduledTimestamp = scheduledDate.getTime();
                                                                const skippedList = Array.isArray(activeServer.scheduled_restarts_skipped) 
                                                                    ? activeServer.scheduled_restarts_skipped 
                                                                    : [];
                                                                
                                                                // Check if any skipped timestamp matches this execution within 1 minute
                                                                const isSkipped = skippedList.some((ts: string) => {
                                                                    try {
                                                                        const skipTime = new Date(ts).getTime();
                                                                        return Math.abs(skipTime - scheduledTimestamp) < 60000;
                                                                    } catch { return false; }
                                                                });

                                                                if (isSkipped) {
                                                                    return (
                                                                        <Badge variant="warning" size="sm" className="mt-1 text-[10px] px-1.5 py-0 h-4">
                                                                            Heute übersprungen
                                                                        </Badge>
                                                                    );
                                                                }
                                                            } catch (e) {
                                                                console.error("Error calculating restart logic:", e);
                                                            }
                                                            return null;
                                                        })()}
                                                    </div>
                                                </div>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-7 w-7 opacity-50 group-hover:opacity-100 hover:bg-red-500/20 hover:text-red-400 transition-all"
                                                    onClick={() => {
                                                        const newSchedules = (activeServer.scheduled_restarts?.schedules || []).filter((_, i) => i !== idx);
                                                        serverStore.handleSaveServer({
                                                            id: activeServer.id,
                                                            scheduled_restarts: { ...activeServer.scheduled_restarts, schedules: newSchedules }
                                                        });
                                                    }}
                                                >
                                                    <Trash2 size={12} />
                                                </Button>
                                            </div>
                                        ))
                                    )}
                                    <div className="flex gap-2 mt-3 pt-3 border-t border-line">
                                        <div className="flex-1 flex gap-1 items-center">
                                            <input
                                                type="text"
                                                id="new-restart-hour"
                                                maxLength={2}
                                                placeholder="00"
                                                className="w-14 text-center bg-black/40 border border-line-strong rounded-lg px-2 py-2.5 text-white text-sm font-mono outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20 transition-all placeholder:text-fg-muted"
                                                onInput={(e) => {
                                                    const input = e.target as HTMLInputElement;
                                                    input.value = input.value.replace(/\D/g, '').slice(0, 2);
                                                    if (parseInt(input.value) > 23) input.value = '23';
                                                }}
                                            />
                                            <span className="text-fg-muted font-bold text-lg">:</span>
                                            <input
                                                type="text"
                                                id="new-restart-min"
                                                maxLength={2}
                                                placeholder="00"
                                                className="w-14 text-center bg-black/40 border border-line-strong rounded-lg px-2 py-2.5 text-white text-sm font-mono outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20 transition-all placeholder:text-fg-muted"
                                                onInput={(e) => {
                                                    const input = e.target as HTMLInputElement;
                                                    input.value = input.value.replace(/\D/g, '').slice(0, 2);
                                                    if (parseInt(input.value) > 59) input.value = '59';
                                                }}
                                            />
                                            <span className="text-fg-muted text-sm ml-2">Uhr</span>
                                        </div>
                                        <Button
                                            variant="secondary"
                                            className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 border-blue-500/30"
                                            onClick={() => {
                                                const hourInput = document.getElementById('new-restart-hour') as HTMLInputElement;
                                                const minInput = document.getElementById('new-restart-min') as HTMLInputElement;
                                                const hour = hourInput.value.padStart(2, '0');
                                                const min = minInput.value.padStart(2, '0');
                                                if (hour && min && parseInt(hour) <= 23 && parseInt(min) <= 59) {
                                                    const timeValue = `${hour}:${min}`;
                                                    const newSchedules = [...(activeServer.scheduled_restarts?.schedules || []), timeValue].sort();
                                                    const uniqueSchedules = Array.from(new Set(newSchedules));
                                                    serverStore.handleSaveServer({
                                                        id: activeServer.id,
                                                        scheduled_restarts: { ...activeServer.scheduled_restarts, schedules: uniqueSchedules }
                                                    });
                                                    hourInput.value = '';
                                                    minInput.value = '';
                                                }
                                            }}
                                        >
                                            <Plus size={16} />
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            {/* Warnings */}
                            <div className="p-4 bg-gradient-to-br from-amber-500/5 to-orange-500/5 rounded-xl border border-amber-500/20">
                                <div className="flex items-center gap-2 mb-4">
                                    <Bell size={16} className="text-amber-400" />
                                    <h5 className="text-sm font-semibold text-amber-300">Warnungen vor Restart</h5>
                                </div>
                                
                                {/* Preset Buttons */}
                                <div className="mb-4 p-3 bg-black/30 rounded-lg border border-line">
                                    <div className="text-[10px] uppercase font-bold text-fg-muted mb-2">Schnell hinzufügen:</div>
                                    
                                    {/* 60s Countdown Toggle */}
                                    <div className="mb-3 p-3 bg-gradient-to-r from-red-500/10 to-orange-500/10 rounded-lg border border-red-500/30">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <div className="text-xs font-bold text-red-300 mb-0.5">60-Sekunden Countdown</div>
                                                <div className="text-[9px] text-fg-muted">Sekündliche Warnungen von 60s bis 1s</div>
                                            </div>
                                            <ToggleSwitch
                                                enabled={(() => {
                                                    const warnings = activeServer.scheduled_restarts?.warnings || [];
                                                    const countdownSeconds = Array.from({ length: 60 }, (_, i) => i + 1);
                                                    return countdownSeconds.every(s => warnings.includes(s));
                                                })()}
                                                onChange={(enabled) => {
                                                    const warnings = activeServer.scheduled_restarts?.warnings || [];
                                                    const countdownSeconds = Array.from({ length: 60 }, (_, i) => i + 1);
                                                    
                                                    if (enabled) {
                                                        // Add all countdown warnings
                                                        const combined = [...warnings, ...countdownSeconds];
                                                        const newWarnings = Array.from(new Set(combined)).sort((a, b) => b - a);
                                                        serverStore.handleSaveServer({
                                                            id: activeServer.id,
                                                            scheduled_restarts: { ...activeServer.scheduled_restarts, warnings: newWarnings }
                                                        });
                                                    } else {
                                                        // Remove all countdown warnings
                                                        const newWarnings = warnings.filter(w => !countdownSeconds.includes(w));
                                                        serverStore.handleSaveServer({
                                                            id: activeServer.id,
                                                            scheduled_restarts: { ...activeServer.scheduled_restarts, warnings: newWarnings }
                                                        });
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>
                                    
                                    <div className="space-y-2">
                                        {/* Minutes Presets */}
                                        <div>
                                            <div className="text-[9px] text-fg-muted mb-1 font-semibold">Minuten:</div>
                                            <div className="flex flex-wrap gap-2">
                                                {[
                                                    { label: '30 Min', value: 30 * 60 },
                                                    { label: '15 Min', value: 15 * 60 },
                                                    { label: '10 Min', value: 10 * 60 },
                                                    { label: '5 Min', value: 5 * 60 },
                                                    { label: '1 Min', value: 60 },
                                                ].map((preset) => {
                                                    const warnings = activeServer.scheduled_restarts?.warnings || [];
                                                    const isAdded = warnings.includes(preset.value);
                                                    return (
                                                        <Button
                                                            key={preset.value}
                                                            variant={isAdded ? "secondary" : "ghost"}
                                                            size="sm"
                                                            className={`text-xs ${
                                                                isAdded 
                                                                    ? 'bg-amber-500/30 text-amber-200 border-amber-500/50' 
                                                                    : 'bg-black/30 hover:bg-amber-500/10 text-fg-muted hover:text-amber-300 border-line'
                                                            }`}
                                                            onClick={() => {
                                                                if (isAdded) {
                                                                    const newWarnings = warnings.filter(w => w !== preset.value);
                                                                    serverStore.handleSaveServer({
                                                                        id: activeServer.id,
                                                                        scheduled_restarts: { ...activeServer.scheduled_restarts, warnings: newWarnings }
                                                                    });
                                                                } else {
                                                                    const newWarnings = [...warnings, preset.value].sort((a, b) => b - a);
                                                                    serverStore.handleSaveServer({
                                                                        id: activeServer.id,
                                                                        scheduled_restarts: { ...activeServer.scheduled_restarts, warnings: newWarnings }
                                                                    });
                                                                }
                                                            }}
                                                        >
                                                            {isAdded ? '✓ ' : '+ '}{preset.label}
                                                        </Button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Warning List */}
                                <div className="space-y-2">
                                    {(() => {
                                        const warnings = activeServer.scheduled_restarts?.warnings || [];
                                        if (warnings.length === 0) {
                                            return <div className="text-xs text-fg-muted italic py-3 text-center">Keine Warnungen konfiguriert</div>;
                                        }

                                        // Check if 60s countdown is active
                                        const countdownSeconds = Array.from({ length: 60 }, (_, i) => i + 1);
                                        const hasFullCountdown = countdownSeconds.every(s => warnings.includes(s));
                                        
                                        // Filter out countdown seconds if full countdown is active
                                        const customWarnings = hasFullCountdown 
                                            ? warnings.filter(w => !countdownSeconds.includes(w))
                                            : warnings;

                                        return (
                                            <>
                                                {/* Show 60s Countdown Summary */}
                                                {hasFullCountdown && (
                                                    <div className="flex items-center justify-between p-3 bg-gradient-to-r from-red-500/10 to-orange-500/10 rounded-lg border border-red-500/30">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center">
                                                                <Bell size={14} className="text-red-400" />
                                                            </div>
                                                            <div>
                                                                <span className="text-white font-medium">60-Sekunden Countdown</span>
                                                                <div className="text-[10px] text-fg-muted">Sekündliche Warnungen von 60s bis 1s</div>
                                                            </div>
                                                        </div>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-7 w-7 opacity-50 hover:opacity-100 hover:bg-red-500/20 hover:text-red-400 transition-all"
                                                            onClick={() => {
                                                                const newWarnings = warnings.filter(w => !countdownSeconds.includes(w));
                                                                serverStore.handleSaveServer({
                                                                    id: activeServer.id,
                                                                    scheduled_restarts: { ...activeServer.scheduled_restarts, warnings: newWarnings }
                                                                });
                                                            }}
                                                        >
                                                            <Trash2 size={12} />
                                                        </Button>
                                                    </div>
                                                )}

                                                {/* Show Custom/Individual Warnings */}
                                                {customWarnings.slice().sort((a, b) => b - a).map((seconds, idx) => {
                                                    const minutes = Math.floor(seconds / 60);
                                                    const secs = seconds % 60;
                                                    let displayText = '';
                                                    if (minutes > 0 && secs > 0) {
                                                        displayText = `${minutes} Min ${secs} Sek`;
                                                    } else if (minutes > 0) {
                                                        displayText = `${minutes} ${minutes > 1 ? 'Minuten' : 'Minute'}`;
                                                    } else {
                                                        displayText = `${secs} ${secs > 1 ? 'Sekunden' : 'Sekunde'}`;
                                                    }

                                                    return (
                                                        <div key={idx} className="flex items-center justify-between p-3 bg-black/30 rounded-lg border border-line group hover:border-amber-500/30 transition-colors">
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                                                                    <Bell size={14} className="text-amber-400" />
                                                                </div>
                                                                <span className="text-white font-medium">{displayText} vorher</span>
                                                            </div>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-7 w-7 opacity-50 group-hover:opacity-100 hover:bg-red-500/20 hover:text-red-400 transition-all"
                                                                onClick={() => {
                                                                    const newWarnings = warnings.filter((w) => w !== seconds);
                                                                    serverStore.handleSaveServer({
                                                                        id: activeServer.id,
                                                                        scheduled_restarts: { ...activeServer.scheduled_restarts, warnings: newWarnings }
                                                                    });
                                                                }}
                                                            >
                                                                <Trash2 size={12} />
                                                            </Button>
                                                        </div>
                                                    );
                                                })}
                                            </>
                                        );
                                    })()}
                                    
                                    {/* Custom Warning Input */}
                                    <div className="pt-3 border-t border-line">
                                        <div className="text-[10px] uppercase font-bold text-fg-muted mb-2">Benutzerdefiniert:</div>
                                        <div className="flex gap-2">
                                            <input
                                                type="number"
                                                id="new-warning-min"
                                                placeholder="Min"
                                                min="0"
                                                className="w-20 bg-black/40 border border-line-strong rounded-lg px-3 py-2.5 text-white text-sm outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 transition-all placeholder:text-fg-muted"
                                            />
                                            <input
                                                type="number"
                                                id="new-warning-sec"
                                                placeholder="Sek"
                                                min="0"
                                                max="59"
                                                className="w-20 bg-black/40 border border-line-strong rounded-lg px-3 py-2.5 text-white text-sm outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 transition-all placeholder:text-fg-muted"
                                            />
                                            <Button
                                                variant="secondary"
                                                className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border-amber-500/30"
                                                onClick={() => {
                                                    const minInput = document.getElementById('new-warning-min') as HTMLInputElement;
                                                    const secInput = document.getElementById('new-warning-sec') as HTMLInputElement;
                                                    const minutes = parseInt(minInput.value) || 0;
                                                    const seconds = parseInt(secInput.value) || 0;
                                                    const totalSeconds = minutes * 60 + seconds;
                                                    
                                                    if (totalSeconds > 0) {
                                                        const newWarnings = [...(activeServer.scheduled_restarts?.warnings || []), totalSeconds].sort((a,b) => b-a);
                                                        const uniqueWarnings = Array.from(new Set(newWarnings));
                                                        serverStore.handleSaveServer({
                                                            id: activeServer.id,
                                                            scheduled_restarts: { ...activeServer.scheduled_restarts, warnings: uniqueWarnings }
                                                        });
                                                        minInput.value = '';
                                                        secInput.value = '';
                                                    }
                                                }}
                                            >
                                                <Plus size={16} />
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </Card>

            {/* Auto-Restart Settings */}
            <Card>
                <h4 className="text-sm font-semibold text-fg mb-4 flex items-center">
                    <RefreshCw size={18} className="mr-2 text-ember" />
                    Auto-Restart bei Absturz
                </h4>
                <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-black/20 rounded-xl border border-line">
                        <div>
                            <div className="text-sm font-medium text-fg">Auto-Restart aktiviert</div>
                            <div className="text-xs text-fg-muted mt-0.5">Server automatisch neu starten wenn er abstürzt</div>
                        </div>
                        <ToggleSwitch
                            enabled={activeServer.auto_restart_config?.enabled || false}
                            onChange={(val) => {
                                serverStore.updateAutoRestartConfig({ enabled: val });
                            }}
                        />
                    </div>

                    {activeServer.auto_restart_config?.enabled && (
                        <>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="p-4 bg-black/20 rounded-xl border border-line">
                                    <div className="text-xs text-fg-muted mb-2">Max. Abstürze</div>
                                    <input
                                        type="number"
                                        min={1}
                                        max={20}
                                        value={activeServer.auto_restart_config?.max_crashes ?? 5}
                                        onChange={(e) => {
                                            serverStore.updateAutoRestartConfig({
                                                max_crashes: parseInt(e.target.value) || 5
                                            });
                                        }}
                                        className="w-full bg-ink/50 border border-line-strong rounded-lg p-2 text-white text-sm outline-none focus:border-ember/50"
                                    />
                                    <div className="text-[10px] text-fg-muted mt-1">
                                        Danach wird Auto-Restart deaktiviert
                                    </div>
                                </div>

                                <div className="p-4 bg-black/20 rounded-xl border border-line">
                                    <div className="text-xs text-fg-muted mb-2">Zeitfenster (Minuten)</div>
                                    <input
                                        type="number"
                                        min={1}
                                        max={60}
                                        value={activeServer.auto_restart_config?.crash_window_minutes ?? 10}
                                        onChange={(e) => {
                                            serverStore.updateAutoRestartConfig({
                                                crash_window_minutes: parseInt(e.target.value) || 10
                                            });
                                        }}
                                        className="w-full bg-ink/50 border border-line-strong rounded-lg p-2 text-white text-sm outline-none focus:border-ember/50"
                                    />
                                    <div className="text-[10px] text-fg-muted mt-1">
                                        Absturz-Zähler wird danach zurückgesetzt
                                    </div>
                                </div>
                            </div>

                            {activeServer.auto_restart_config?.disabled_until && (
                                <div className="p-4 bg-red-900/20 rounded-xl border border-red-500/20">
                                    <div className="flex items-center gap-2 text-red-400 text-sm font-medium">
                                        <AlertTriangle size={16} />
                                        Auto-Restart vorübergehend deaktiviert
                                    </div>
                                    <div className="text-xs text-red-300/70 mt-1">
                                        Zu viele Abstürze erkannt. Reaktivierung: {new Date(activeServer.auto_restart_config.disabled_until).toLocaleString('de-DE')}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </Card>
        </div>
    );
});
